/**
 * ExtractionPanel 组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useState, useEffect, useCallback } from 'react'
import { useChatStore } from '../store/chatStore'
import { memoryAdminApi } from '../services/memory-admin-api'
import { CheckCircle, ArrowRight, Loader2, RefreshCw, User, Users, MapPin, Heart, Briefcase, Sparkles } from 'lucide-react'
import type { Entity, Relationship } from '../types/memory'

// 实体类型颜色
const TYPE_COLORS: Record<string, string> = {
  User: '#AF52DE',
  Person: '#007AFF',
  Name: '#007AFF',
  Family: '#FF2D55',
  Friend: '#34C759',
  City: '#5AC8FA',
  Hobby: '#AF52DE',
  Occupation: '#FF9500',
  Other: '#8E8E93',
}

// 实体类型配置
const ENTITY_CONFIG: Record<string, { label: string; color: string; icon: typeof User }> = {
  User: { label: '用户', color: '#AF52DE', icon: User },
  Person: { label: '人物', color: '#007AFF', icon: User },
  Name: { label: '姓名', color: '#007AFF', icon: User },
  Family: { label: '家人', color: '#FF2D55', icon: Users },
  Friend: { label: '朋友', color: '#34C759', icon: Users },
  City: { label: '城市', color: '#5AC8FA', icon: MapPin },
  CurrentCity: { label: '城市', color: '#5AC8FA', icon: MapPin },
  Hobby: { label: '爱好', color: '#AF52DE', icon: Heart },
  Occupation: { label: '职业', color: '#FF9500', icon: Briefcase },
  Company: { label: '公司', color: '#FF9500', icon: Briefcase },
  Other: { label: '其他', color: '#8E8E93', icon: User },
}

const getTypeColor = (type: string) => TYPE_COLORS[type] || TYPE_COLORS.Other
const getEntityConfig = (type: string) => ENTITY_CONFIG[type] || ENTITY_CONFIG.Other

interface ExtractionPanelProps {
  userId?: string
  projectId?: string
}

export default function ExtractionPanel({ userId, projectId }: ExtractionPanelProps) {
  const { extractionResult, isExtracting } = useChatStore()
  
  const [entities, setEntities] = useState<Entity[]>([])
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [isLoadingEntities, setIsLoadingEntities] = useState(false)
  const [lastExtractionTime, setLastExtractionTime] = useState<string | null>(null)
  const [recentEntities, setRecentEntities] = useState<Entity[]>([])
  const [recentRelationships, setRecentRelationships] = useState<Relationship[]>([])

  // 加载已存储实体和关系
  const loadEntitiesAndRelations = useCallback(async () => {
    if (!userId) return
    setIsLoadingEntities(true)
    try {
      const [entitiesRes, relationshipsRes] = await Promise.all([
        memoryAdminApi.getEntities({ user_id: userId, project_id: projectId, limit: 50 }),
        memoryAdminApi.getRelationships({ user_id: userId, project_id: projectId, limit: 50 }),
      ])
      
      console.log('📦 [ExtractionPanel] getEntities 后端返回:', JSON.stringify(entitiesRes, null, 2))
      console.log('📦 [ExtractionPanel] getRelationships 后端返回:', JSON.stringify(relationshipsRes, null, 2))
      
      setEntities(entitiesRes.entities)
      setRelationships(relationshipsRes.relationships || [])
    } catch (error) {
      console.error('Failed to load entities:', error)
    } finally {
      setIsLoadingEntities(false)
    }
  }, [userId, projectId])

  // 初始加载
  useEffect(() => {
    loadEntitiesAndRelations()
  }, [loadEntitiesAndRelations])

  // 提取完成后刷新并显示最新提取的内容
  useEffect(() => {
    if (extractionResult && !isExtracting) {
      console.log('📊 [ExtractionPanel] 收到 extractionResult:', JSON.stringify(extractionResult, null, 2))
      console.log('📊 [ExtractionPanel] relationships 原始数据:', JSON.stringify(extractionResult.relationships, null, 2))
      
      if (extractionResult.entities) {
        setRecentEntities(extractionResult.entities)
      }
      if (extractionResult.relationships) {
        console.log('📊 [ExtractionPanel] 设置 recentRelationships, 数量:', extractionResult.relationships.length)
        extractionResult.relationships.forEach((rel, idx) => {
          console.log(`📊 [ExtractionPanel] 关系[${idx}]:`, JSON.stringify({
            id: rel.id,
            type: rel.type,
            source_entity_id: rel.source_entity_id,
            target_entity_id: rel.target_entity_id,
            source_name: rel.source_name,
            target_name: rel.target_name,
            hasSourceName: 'source_name' in rel,
            hasTargetName: 'target_name' in rel,
          }, null, 2))
        })
        setRecentRelationships(extractionResult.relationships)
      }
      setLastExtractionTime(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }))
      
      // 延迟刷新完整列表
      const timer = setTimeout(() => loadEntitiesAndRelations(), 500)
      return () => clearTimeout(timer)
    }
  }, [extractionResult, isExtracting, loadEntitiesAndRelations])

  return (
    <aside className="w-72 flex flex-col h-full bg-card border-l border-border shadow-sm">
      {/* Header - 提取结果 */}
      <div className="p-4 border-b border-border">
        <h2 className="font-medium flex items-center gap-2 text-foreground">
          {isExtracting ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          ) : (
            <CheckCircle className="w-4 h-4 text-green-500" />
          )}
          提取结果
        </h2>
        <p className="text-xs mt-1 text-muted-foreground">本次会话提取的信息</p>
      </div>

      {/* 本次提取的实体 */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground">实体</h3>
          {lastExtractionTime && (
            <span className="text-[10px] flex items-center gap-1 text-muted-foreground">
              <Sparkles className="w-3 h-3" />
              {lastExtractionTime}
            </span>
          )}
        </div>
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          {isExtracting ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : recentEntities.length > 0 ? (
            recentEntities.map((entity, idx) => (
              <div
                key={entity.id || idx}
                className="flex items-center gap-2 p-2 rounded-xl bg-secondary border border-border animate-slide-up shadow-sm"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: getTypeColor(entity.type) }}
                />
                <span className="text-sm truncate flex-1 text-foreground">{entity.name}</span>
                <span className="text-xs text-muted-foreground">{entity.type}</span>
              </div>
            ))
          ) : extractionResult?.entities?.length ? (
            extractionResult.entities.map((entity, idx) => (
              <div
                key={entity.id || idx}
                className="flex items-center gap-2 p-2 rounded-xl bg-secondary border border-border animate-slide-up shadow-sm"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: getTypeColor(entity.type) }}
                />
                <span className="text-sm truncate flex-1 text-foreground">{entity.name}</span>
                <span className="text-xs text-muted-foreground">{entity.type}</span>
              </div>
            ))
          ) : (
            <p className="text-center text-sm py-2 text-muted-foreground">
              发送消息后自动提取
            </p>
          )}
        </div>
      </div>

      {/* 本次提取的关系 */}
      <div className="p-4 border-b border-border">
        <h3 className="text-xs uppercase tracking-wider mb-3 text-muted-foreground">关系</h3>
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          {isExtracting ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : recentRelationships.length > 0 ? (
            recentRelationships.map((rel, idx) => (
              <div
                key={rel.id || idx}
                className="p-2.5 rounded-xl text-xs bg-secondary border border-border animate-slide-up shadow-sm"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="flex items-center justify-center mb-0.5">
                  <span className="text-[10px] font-medium text-blue-400">
                    {rel.type || '相关'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium text-foreground max-w-[70px]">
                    {rel.source_name || '?'}
                  </span>
                  <div className="flex-1 flex items-center">
                    <div className="flex-1 h-px bg-blue-500/50" />
                    <ArrowRight className="w-3.5 h-3.5 -ml-1 text-blue-500" />
                  </div>
                  <span className="truncate font-medium text-foreground max-w-[70px]">
                    {rel.target_name || '?'}
                  </span>
                </div>
              </div>
            ))
          ) : extractionResult?.relationships?.length ? (
            extractionResult.relationships.map((rel, idx) => (
              <div
                key={rel.id || idx}
                className="p-2.5 rounded-xl text-xs bg-secondary border border-border animate-slide-up shadow-sm"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="flex items-center justify-center mb-0.5 relative">
                  <span className="text-[10px] font-medium text-blue-400">
                    {rel.type || '相关'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium text-foreground max-w-[70px]">
                    {rel.source_name || '?'}
                  </span>
                  <div className="flex-1 flex items-center">
                    <div className="flex-1 h-px bg-blue-500/50" />
                    <ArrowRight className="w-3.5 h-3.5 -ml-1 text-blue-500" />
                  </div>
                  <span className="truncate font-medium text-foreground max-w-[70px]">
                    {rel.target_name || '?'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-sm py-2 text-muted-foreground">
              发送消息后自动提取
            </p>
          )}
        </div>
      </div>

      {/* 已存储实体 */}
      <div className="flex-1 overflow-hidden flex flex-col p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground">已存储实体</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={loadEntitiesAndRelations}
              disabled={isLoadingEntities}
              className="p-1 rounded transition-colors hover:opacity-80 text-muted-foreground disabled:opacity-50"
              title="刷新"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoadingEntities ? 'animate-spin' : ''}`} />
            </button>
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
              {entities.length}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-1.5">
          {isLoadingEntities ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : entities.length === 0 ? (
            <p className="text-center text-sm py-4 text-muted-foreground">暂无数据</p>
          ) : (
            entities.map((entity, index) => {
              const config = getEntityConfig(entity.type)
              const Icon = config.icon
              const confidence = entity.confidence ?? 0.8
              const confidencePercent = Math.round(confidence * 100)
              return (
                <div
                  key={entity.id || `entity-${index}`}
                  className="p-2 rounded-xl transition-colors hover:opacity-90 bg-secondary border border-border shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                         style={{ background: `${config.color}20` }}>
                      <Icon className="w-3 h-3" style={{ color: config.color }} />
                    </div>
                    <span className="text-sm truncate flex-1 text-foreground">{entity.name}</span>
                    <div className="flex items-center gap-1">
                      <div 
                        className="w-6 h-1 rounded-full overflow-hidden bg-muted"
                        title={`置信度: ${confidencePercent}%`}
                      >
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${confidencePercent}%`,
                            background: confidence >= 0.8 ? '#34C759' : confidence >= 0.5 ? '#FF9500' : '#FF3B30'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* 已存储关系 */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground">已存储关系</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
            {relationships.length}
          </span>
        </div>
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          {relationships.length === 0 ? (
            <p className="text-center text-sm py-2 text-muted-foreground">暂无数据</p>
          ) : (
            relationships.map((rel, idx) => (
              <div
                key={rel.id || idx}
                className="p-2.5 rounded-xl text-xs bg-secondary border border-border shadow-sm"
              >
                <div className="flex items-center justify-center mb-0.5">
                  <span className="text-[10px] font-medium text-purple-400">
                    {rel.relation_name || rel.type || '相关'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium text-foreground max-w-[70px]">
                    {rel.source_name || '?'}
                  </span>
                  <div className="flex-1 flex items-center">
                    <div className="flex-1 h-px bg-purple-500/50" />
                    <ArrowRight className="w-3.5 h-3.5 -ml-1 text-purple-500" />
                  </div>
                  <span className="truncate font-medium text-foreground max-w-[70px]">
                    {rel.target_name || '?'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Stats */}
      {extractionResult && (
        <div className="p-4 border-t border-border">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">本次实体</span>
            <span className="text-foreground">{extractionResult.entities?.length || 0}</span>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-muted-foreground">本次关系</span>
            <span className="text-foreground">{extractionResult.relationships?.length || 0}</span>
          </div>
        </div>
      )}
    </aside>
  )
}

